/**
 * Thrivia Embed SDK: the page surface.
 *
 * Purpose: put a whole Thrivia event page inside an organizer's own layout, at the height of its
 * content rather than in a scrolling box.
 *
 * IT IS AN IFRAME AND NOT LIGHT DOM, unlike the card and list surfaces, and that is the one design
 * decision worth stating. A full event page is a gallery, an itinerary, a coupon box, a ticket
 * picker and a payment handoff; rendering that into somebody else's document would mean shipping
 * the whole storefront in this bundle, inheriting their CSS on all of it, and running Razorpay
 * inside a document whose parent is theirs (R18: checkout.js broadcasts the buyer's email and phone
 * to `window.parent`). The frame keeps all three problems on our side of an origin boundary.
 *
 * NO NEW IFRAME CODE (R37). The element comes from `overlay.ts`'s `newFrame`, so `allow="payment"`
 * is decided in exactly one place, and the URL tail comes from its `frameQuery`, so `parent_origin`
 * and the sid are built the same way for every surface. Heights arrive on the existing bus through
 * `onBus`, which supports several subscribers; there is no second `message` listener anywhere.
 *
 * THE SRC IS SET LATE, AND THAT IS THE POINT OF THE TWO PHASES. `mount()` runs at discovery and
 * paints an empty box of the reserved height, so the organizer's layout does not jump when the
 * frame arrives. The attribution sid does not exist yet: it is minted by the API response that the
 * batched read is still waiting for. `update()` sets `src` once that has landed, so the page that
 * loads is one that can attribute a booking. A frame loaded at mount time would be permanently
 * un-attributable, and the sid cannot be delivered afterwards without inventing a bus message for
 * it.
 *
 * No runtime dependencies, DOM only, ES2019 output target.
 */

import { emitBooking, onBus, post, type BusPayload, type BusType } from "../bus";
import { OWN, swap } from "./card";
import { cover, frameQuery, loginPopup, newFrame, refused } from "../overlay";
import type { MountRecord, MountRenderer } from "../scan";
import { injectStyles } from "../styles";

/**
 * The reserved box, inline rather than a stylesheet rule.
 *
 * Three declarations used in one place cost less inline than a rule plus a selector plus a class
 * name, and inline survives an organizer whose CSP blocked our `<style>`. Width and the reset come
 * from `.thrivia-r`, which this element carries. 640px is a first paint that fits a phone viewport
 * without dwarfing a desktop column; the real height arrives on the first `resize`.
 */
const BOX = "height:640px;border-radius:16px";

/**
 * Ceiling on a height we will apply.
 *
 * The frame is ours, so this is not a trust boundary; it is a bug boundary. A measurement error
 * that asked for a million pixels would give the organizer a page they cannot scroll past, on their
 * own website, with no way to fix it.
 */
const MAX_HEIGHT = 20000;

/**
 * How long a page frame has to say anything before it is presumed refused. TWELVE SECONDS, and it
 * is deliberately NOT the checkout overlay's four.
 *
 * Sharing one number with the overlay was tidy and it was wrong, because the two frames have
 * different worst cases and only one of them is instant. The overlay's four seconds is right for
 * what it times: `/embed/checkout` is a client-rendered route, and its dominant failure is a
 * `frame-ancestors` refusal, which the browser delivers before any network at all, so waiting
 * longer only makes an unregistered site stare at nothing for longer.
 *
 * `/embed/e/[id]` is a different shape of route. Its SSR awaits a permalink resolve (3s budget) and
 * THEN a fetch by slug (5s budget), sequentially, before it emits a byte of HTML, with a framing
 * lookup (2s) on the same request. A cold, slow, worst case is therefore around ten seconds of a
 * frame that is working perfectly and simply has not answered yet. Timing that at four means the
 * ordinary slow case was being reported to a correctly-registered organizer as "add your domain in
 * your Thrivia dashboard", which is wrong advice, confidently given, about a setting they already
 * have right. Twelve clears the real worst case with a margin rather than sitting on it.
 *
 * The number is only half the fix; the other half is that this timeout is now RECOVERABLE. See
 * `update`. A slow frame that answers at second fourteen heals itself, so being wrong about this
 * number is no longer permanent, which is what allows it to be a judgement rather than a guess.
 */
const PAGE_TIMEOUT_MS = 12000;

let appOrigin = "";
let publishableKey = "";
let mounts: MountRecord[] = [];

/**
 * The login popup this surface opened, and the mount whose frame asked for it.
 *
 * Held here rather than read off the overlay's own state because the answer this surface needs is
 * "which of my frames is waiting for this login", which the overlay has no way to know: it tracks
 * one checkout, and a host page can carry several page mounts. Both are cleared implicitly by being
 * overwritten on the next request; a stale pair costs nothing, since a relay is only made for a
 * message from the popup window itself and only to a frame the record still holds.
 */
let loginWin: Window | null = null;
let loginFor: MountRecord | null = null;

/**
 * Called once at boot, after the key guard, so neither string can be null here.
 *
 * The registry's live list comes in with them, and that is what saves this surface a second
 * bookkeeping structure: `pruneMounts` already drops a record when a single-page-application host
 * removes its container, so the frame reference goes with it and there is nothing to release. A
 * private array of frames would have needed a `teardown` to stay in step with a list that is
 * already correct.
 */
export function initPage(origin: string, key: string, list: MountRecord[]): void {
  appOrigin = origin;
  publishableKey = key;
  mounts = list;
}

let subscribed = false;

/**
 * Marks the frame that spoke alive, and applies a height when that is what it said.
 *
 * Matched on `source`, the window the message came from, because a page can carry several page
 * mounts and they all answer from the same origin. The bus has already validated that origin; this
 * is the narrower question of WHICH of our frames spoke.
 *
 * LIVENESS IS SET ON ANY MESSAGE, NOT ONLY ON A USABLE `resize`, which is the same contract the
 * checkout overlay has always had: a frame that only ever posts `error` is still a frame that
 * loaded OUR document rather than being refused by the browser. Marking it only on a valid height
 * would declare a live-but-unhappy frame dead and replace a working page with an apology.
 *
 * `height` is type-checked rather than trusted: the bus validates the envelope and not the payload
 * fields, so a number is a thing to confirm, not to assume.
 *
 * IT ALSO HANDLES `booking:complete` AND `login:open`, WHICH IS NOT AN OVERREACH INTO THE OVERLAY'S
 * JOB — it is the only place either one can be handled at all, and for months neither was.
 * `overlay.ts`'s `known()` believes a message only from the checkout window or the login popup it
 * opened, and a page mount's frame is neither, so every message from this surface was dropped before
 * the type switch. Meanwhile the route inside the frame is the SAME `EmbedFrame` the checkout uses
 * and posts both messages regardless of mode. The result an organizer actually got: a visitor books
 * through a `data-thrivia-mode="page"` mount, the confirmation paints, and `thrivia:booking:complete`
 * never fires, so their GA4 and Meta conversions record nothing. On the login side, a blocked popup
 * reached nobody and the visitor was simply stuck.
 *
 * The fix is deliberately NOT to widen `known()`. That check exists to make window identity the
 * thing we believe rather than origin alone, and every `/embed/*` page answers from our origin, so
 * loosening it would readmit any app-origin document an organizer happens to frame. The loop below
 * is the same proof in the stronger form: a message is acted on only after it has been matched to a
 * SPECIFIC registered mount by `frame.contentWindow !== source`.
 */
function receive(type: BusType, payload: BusPayload, source: unknown): void {
  if (!source) return;
  // The login popup, which is not one of our frames and never matches the loop below. It talks to
  // its opener, which is us, and the frame that asked for the login is the one that needs to know.
  if (source === loginWin) {
    const frame = loginFor && loginFor.frame;
    if (type === "login:complete" && frame) post(frame.contentWindow, "login:complete", payload);
    return;
  }
  const height = payload.height;
  const px =
    type === "resize" && typeof height === "number" && height > 0 && height <= MAX_HEIGHT
      ? height + "px"
      : "";
  for (let i = 0; i < mounts.length; i++) {
    const record = mounts[i];
    const frame = record.frame;
    if (!frame || frame.contentWindow !== source) continue;
    record.live = true;
    // LATE RECOVERY. The liveness timer may already have covered this frame with the "register your
    // domain" panel; the frame answering is proof that it was never refused, only slow, so the
    // panel comes off and the working page comes back. See `update` for why the frame is covered
    // rather than replaced, which is what makes this possible at all.
    if (record.uncover) {
      record.uncover();
      record.uncover = undefined;
    }
    if (px) frame.style.height = px;
    // A frame that just spoke is a frame that can be spoken to, and one that just grew has a new
    // band. Both are answered by measuring again.
    schedule();
    if (type === "booking:complete") emitBooking(payload);
    else if (type === "login:open") askLogin(record);
  }
}

/**
 * Tells every loaded page frame where the visitor's window is, in the FRAME's own coordinates.
 *
 * WHY THIS SURFACE NEEDS IT AND NO OTHER DOES. The checkout overlay's frame IS the viewport: it is
 * `position: fixed` at the window's size, so `fixed` inside it already lands where the visitor is
 * looking. A page mount is the opposite: the frame is grown to the height of its whole content so
 * it sits in the organizer's layout without an inner scrollbar, and `fixed` inside it then resolves
 * against a box that can be 4000px tall. Tapping the sticky "Book Now" at the bottom of such a
 * mount dimmed the organizer's page and drew the booking dialog at the FRAME's vertical centre,
 * around 2000px above the visitor's window. They saw a greyed-out page and no dialog at all.
 *
 * THE OTHER CANDIDATE FIX WAS TO SCROLL THE HOST PAGE so the dialog comes into view, and it was
 * rejected: it is cheaper in bytes but it moves a page the visitor is holding, it fights their very
 * next scroll, and one scroll after opening puts the dialog off-screen again — so it answers "can
 * they see it the instant they tap" and not "can they see it while they use it". Reporting the band
 * answers both, and it answers them for every `fixed` layer at once rather than for one dialog.
 *
 * The maths is an intersection and nothing more: `-box.top` is how much of the frame has already
 * scrolled off the top, and `innerHeight - box.top` is how far down the frame the window's bottom
 * edge falls. A frame entirely off-screen yields nothing and posts nothing, which leaves the last
 * band in place — correct, because a dialog nobody can see needs no repositioning.
 */
function band(): void {
  const seen = window.innerHeight;
  for (let i = 0; i < mounts.length; i++) {
    const frame = mounts[i].frame;
    // `src` unset is a reserved box that has not loaded yet: there is no document to tell.
    if (!frame || !frame.src) continue;
    const box = frame.getBoundingClientRect();
    const top = Math.max(0, -box.top);
    const height = Math.min(box.height, seen - box.top) - top;
    if (height > 0) post(frame.contentWindow, "viewport", { top: top, height: height });
  }
}

/**
 * One measurement per animation frame, however many scroll events arrive.
 *
 * Scroll fires far faster than anything can be painted, and each measurement here is a forced
 * layout read plus a cross-document post per mount. A frame is both the fastest the visitor can
 * perceive a change and the point at which the browser was going to do layout anyway.
 */
let pending = false;
function schedule(): void {
  if (pending) return;
  pending = true;
  requestAnimationFrame(function (): void {
    pending = false;
    band();
  });
}

/**
 * The login popup for one page mount, built from THAT mount's own sid.
 *
 * Per record rather than from anything module level: a host page can carry several page mounts,
 * each with the attribution session its own data arrived under, and a login opened under the wrong
 * one would attribute whatever follows to the wrong surface. The URL is built here from our own
 * configuration and never from anything in the message; an origin-validated message is still not a
 * safe source for a URL.
 *
 * `loginPopup` owns the refusal, which is the common case, and covers the frame instead of
 * destroying it (R53).
 */
function askLogin(record: MountRecord): void {
  const frame = record.frame;
  if (!frame) return;
  loginFor = record;
  loginWin = loginPopup(
    appOrigin + "/embed/login?" + frameQuery(publishableKey, record.sid),
    frame,
  );
}

/** Paints the reserved box. Runs at discovery, before any data exists. */
function mount(record: MountRecord): void {
  injectStyles();
  const frame = newFrame(null, OWN, "Thrivia event");
  frame.style.cssText = BOX;
  record.frame = frame;
  // `swap` hides the organizer's baked permalink anchor while we are showing something and hands it
  // back the moment we are not, which is the same failure posture every other surface has.
  swap(record.el, frame);

  if (subscribed) return;
  subscribed = true;
  onBus(receive);
  // CAPTURE on scroll, because the organizer's own layout decides what scrolls: a mount inside
  // their sticky panel or overflow container moves without the window ever scrolling, and a
  // non-capturing window listener never hears those. Passive: this only reads geometry, and a
  // listener that cannot preventDefault is one the browser need not wait for before scrolling.
  addEventListener("scroll", schedule, { capture: true, passive: true });
  addEventListener("resize", schedule, { passive: true });
}

/**
 * Loads the frame, once, as soon as the batched read has answered.
 *
 * `frame.src` being set is the "already loaded" flag: re-assigning it would reload the page inside,
 * throwing away a visitor's half-filled ticket selection on any later state change.
 *
 * `error` and `missing` still load. The state describes OUR read of the card data, and the frame
 * fetches the event itself: a batched request that failed says nothing about whether the event page
 * renders, and an empty box where an event page was promised is worse than a page that explains
 * itself.
 */
function update(record: MountRecord): void {
  const frame = record.frame;
  if (!frame || frame.src || !record.eventId) return;
  // Wait for the sid QUESTION to be settled, not for a sid to exist. `sid: null` is a legitimate
  // answer meaning "render but do not track" (an origin the key has not learned, a key at its
  // origin cap, preview mode), and treating it as "not ready yet" would leave an empty box on
  // exactly the pages a creator is most likely to be testing. Only `pending` and `loading` are
  // unsettled; `ready`, `missing` and `error` have all had their answer.
  if (record.state === "pending" || record.state === "loading") return;
  const id = encodeURIComponent(record.eventId);
  frame.src = appOrigin + "/embed/e/" + id + "?" + frameQuery(publishableKey, record.sid);

  // THE LIVENESS CONTRACT THIS SURFACE NEVER HAD, and the box it painted is exactly why it needs
  // one. The card and list surfaces fail back to the organizer's own baked permalink anchor and the
  // overlay has had a frame timeout since it shipped; this surface had neither. A refused frame
  // therefore left a 640px empty hole on the organizer's homepage, permanently, with their anchor
  // hidden underneath it and nothing in any console. Their first signal was a customer complaint.
  //
  // A refusal is now the ORDINARY failure here rather than an exotic one: `frame-ancestors` for
  // this route is built from the key's registered domains, so every organizer who pasted the
  // snippet without registering their domain gets refused, instantly, before any network.
  //
  // Read `record.frame !== frame` and not just `record.frame`: a single-page-application host that
  // tore this mount down and re-mounted it has a NEW frame on the record, and a stale timer must
  // not paint an apology over a frame that is loading fine.
  //
  // THE VERDICT IS PROVISIONAL, and it used to be permanent. The old callback released
  // `record.frame` BEFORE swapping the panel in, and `receive` skips any record with no frame, so a
  // frame that answered a second late could neither mark itself live nor get its page back: a
  // correctly-registered organizer whose route was merely slow was told, for the life of the page,
  // to go and register a domain they had already registered. Nothing about that was recoverable and
  // nothing about it was true.
  //
  // So the frame is COVERED, not replaced (see `overlay.ts`'s `cover`): it stays on the record, it
  // stays in the document, and it keeps loading underneath. The undo goes on the record, and the
  // first message from that frame calls it. The visitor sees a message with a way out while we are
  // unsure, and their event page the moment we are wrong.
  setTimeout(function (): void {
    if (record.live || record.frame !== frame || record.uncover) return;
    // The permanent `/e/<id>` permalink, on our own origin, built from the id we just encoded. It
    // is the same destination the generated snippet bakes into the organizer's own anchor, and it
    // is available whether or not the batched read ever answered, which the card URL is not.
    record.uncover = cover(frame, refused(appOrigin + "/e/" + id));
  }, PAGE_TIMEOUT_MS);
}

/**
 * No `teardown`. Everything this renderer creates lives INSIDE `record.el` and on the record
 * itself, so a host that removes its container takes the frame with it and `pruneMounts` drops the
 * record `receive` would have found it through. There is no window listener of our own (the bus
 * owns the only one) and no observer, so there is nothing left to release.
 *
 * The liveness timer is the one thing that outlives a prune, and deliberately needs no cancelling:
 * it closes over the record rather than the registry, so on expiry it swaps a panel into an element
 * that has already left the document. That costs a few nodes nobody sees, once, and buys the
 * absence of a teardown that has to be kept in step with `update`. A `clearTimeout` would be the
 * second mechanism for one decision that `presumeDead` documents the cost of.
 */
export const pageRenderer: MountRenderer = { mount, update };
